import { getCategoryById, getPlaybookById } from "@/data/playbooks/registry";
import { MockGenieConfig } from "@/types/mockups/genie-setup-v2";

import { personalityDimensions, sampleTestPrompts } from "./data";

const findOption = (dimensionId: string, optionId: string | null) => {
  if (!optionId) return null;
  const dim = personalityDimensions.find((d) => d.id === dimensionId);
  return dim?.options.find((o) => o.id === optionId) ?? null;
};

export const composeSystemPrompt = (config: MockGenieConfig): string => {
  const segments: string[] = [];

  const category = config.job.categoryId ? getCategoryById(config.job.categoryId) : null;

  if (category) {
    segments.push(
      `## Your Job\nYou are a genie deployed to ${category.name.toLowerCase()}. ${category.description}`,
    );
  }

  const personalityLines: string[] = [];
  for (const dim of personalityDimensions) {
    const value = config.personality[dim.id];
    const option = findOption(dim.id, value);
    if (option) personalityLines.push(`- ${dim.label}: ${option.promptFragment}`);
  }

  if (personalityLines.length > 0) {
    segments.push(`## Personality\n${personalityLines.join("\n")}`);
  }

  const playbooks = config.playbookIds
    .map((id) => getPlaybookById(id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  if (playbooks.length > 0) {
    const playbookLines = playbooks
      .map((p) => `- ${p.meta.name}: ${p.meta.tagline}`)
      .join("\n");
    segments.push(
      `## Plays You Can Run\nYou know how to execute the following plays:\n${playbookLines}`,
    );
  }

  if (config.knowledge.sources.length > 0) {
    segments.push(
      `## Knowledge\nYou have access to: ${config.knowledge.sources.join(", ")}. Always answer from this knowledge first.`,
    );
  }

  if (config.knowledge.customContext.trim().length > 0) {
    segments.push(`## Context From The Business\n${config.knowledge.customContext.trim()}`);
  }

  return segments.join("\n\n");
};

const findOptionLabel = (dimensionId: string, optionId: string | null) => {
  const opt = findOption(dimensionId, optionId);
  return opt?.label ?? "Not set";
};

const energyExclamation = (energy: string | null) => {
  if (energy === "excited") return "!";
  if (energy === "calm") return ".";
  return "";
};

const greetingByApproach = (approach: string | null, formality: string | null) => {
  const casual = formality === "casual";
  if (approach === "empathetic") {
    return casual ? "Totally hear you" : "I understand";
  }
  if (approach === "socratic") {
    return casual ? "Good one" : "Excellent question";
  }
  return casual ? "Sure thing" : "Of course";
};

const phrasingByFormality = (formality: string | null) => {
  if (formality === "formal") return { fillerOK: "Certainly", followUp: "Is there anything further I may assist with?" };
  if (formality === "casual") return { fillerOK: "Yeah", followUp: "Anything else?" };
  return { fillerOK: "Absolutely", followUp: "Anything else I can help with?" };
};

export const simulateResponse = (
  userMessage: string,
  config: MockGenieConfig,
): string => {
  const length = config.personality.length ?? "balanced";
  const energy = config.personality.energy;
  const exclam = energyExclamation(energy);
  const greeting = greetingByApproach(config.personality.approach, config.personality.formality);
  const phrasing = phrasingByFormality(config.personality.formality);
  const lower = userMessage.toLowerCase();

  let core = "I can help with that";
  if (lower.includes("pricing")) core = "Our pricing scales with the number of conversations you run each month";
  else if (lower.includes("book") || lower.includes("call")) core = "I can get a call on the calendar for you";
  else if (lower.includes("trouble") || lower.includes("problem") || lower.includes("help"))
    core = "Let's figure that out together";
  else if (lower.includes("start") || lower.includes("get going") || lower.includes("started"))
    core = "I'll walk you through the very first step";
  else if (lower.includes("what can you") || lower.includes("what do you do"))
    core = "I can answer your questions and walk you through what you need";

  const opener = `${greeting}${exclam || "."} ${core}`;

  if (length === "short") {
    return `${opener}${exclam || "."}`;
  }

  if (length === "balanced") {
    return `${opener}${exclam || "."} ${phrasing.followUp}`;
  }

  return `${opener}${exclam || "."} Here's how I'd approach it: I'll start with the basics, check in to make sure each step is clear, and we'll move at whatever pace works for you. ${phrasing.followUp}`;
};

export const getConfigSummary = (config: MockGenieConfig) => {
  const category = config.job.categoryId ? getCategoryById(config.job.categoryId) : null;
  return {
    jobName: category?.name ?? "Not set",
    role: findOptionLabel("role", config.personality.role),
    length: findOptionLabel("length", config.personality.length),
    energy: findOptionLabel("energy", config.personality.energy),
    approach: findOptionLabel("approach", config.personality.approach),
    formality: findOptionLabel("formality", config.personality.formality),
    playbookCount: config.playbookIds.length,
    knowledgeCount: config.knowledge.sources.length,
  };
};

export const isStepComplete = (
  stepId: string,
  config: MockGenieConfig,
): boolean => {
  switch (stepId) {
    case "job":
      return Boolean(config.job.categoryId);
    case "personality":
      return Object.values(config.personality).every((v) => v !== null);
    case "playbooks":
      return config.playbookIds.length > 0;
    case "knowledge":
      return (
        config.knowledge.sources.length > 0 ||
        config.knowledge.customContext.trim().length > 0
      );
    case "preview":
    case "review":
      return true;
    default:
      return false;
  }
};

export const getInitialConfig = (): MockGenieConfig => ({
  job: { categoryId: null },
  personality: {
    role: null,
    length: null,
    energy: null,
    approach: null,
    formality: null,
  },
  playbookIds: [],
  knowledge: { sources: [], customContext: "" },
  welcomeMessage: "",
  genieName: "",
});

export { sampleTestPrompts };
